import { useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, X, Save, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminImportPDF() {
  const { authHeaders } = useAuth();
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState({});
  const [results, setResults] = useState({}); // { fileName: { quotes: [...], expanded: true } }
  const [vehicles, setVehicles] = useState([]);
  const [successCount, setSuccessCount] = useState(0);

  const fetchVehicles = useCallback(async () => {
    const res = await axios.get(`${API}/vehicles`);
    setVehicles(res.data);
  }, []);

  const handleFilesChange = (newFiles) => {
    const pdfFiles = Array.from(newFiles).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length === 0) return;
    
    setFiles(prev => {
      const existing = prev.map(f => f.name);
      const toAdd = pdfFiles.filter(f => !existing.includes(f.name));
      return [...prev, ...toAdd];
    });
  };

  const removeFile = (fileName) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
    setResults(prev => {
      const copy = { ...prev };
      delete copy[fileName];
      return copy;
    });
  };

  const analyzeFile = async (file) => {
    setProcessing(prev => ({ ...prev, [file.name]: true }));
    
    try {
      if (vehicles.length === 0) await fetchVehicles();
      
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/devis/import-pdf`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      
      // Handle multiple quotes from single PDF
      const quotes = res.data.quotes.map((q, idx) => ({
        id: `${file.name}-${idx}`,
        status: 'extracted',
        data: q.extracted_data,
        matchedVehicle: q.matched_vehicle,
        form: {
          ...q.extracted_data,
          vehicle_id: q.matched_vehicle?.id || "",
          statut: "réservé",
        },
      }));
      
      setResults(prev => ({
        ...prev,
        [file.name]: {
          quotes,
          expanded: true,
          count: quotes.length,
        }
      }));
    } catch (err) {
      setResults(prev => ({
        ...prev,
        [file.name]: {
          quotes: [{
            id: `${file.name}-error`,
            status: 'error',
            error: err.response?.data?.detail || "Erreur lors de l'analyse"
          }],
          expanded: true,
          count: 0,
        }
      }));
    } finally {
      setProcessing(prev => ({ ...prev, [file.name]: false }));
    }
  };

  const analyzeAll = async () => {
    if (vehicles.length === 0) await fetchVehicles();
    for (const file of files) {
      if (!results[file.name]) {
        await analyzeFile(file);
      }
    }
  };

  const toggleExpanded = (fileName) => {
    setResults(prev => ({
      ...prev,
      [fileName]: { ...prev[fileName], expanded: !prev[fileName]?.expanded }
    }));
  };

  const updateQuoteForm = (fileName, quoteId, key, value) => {
    setResults(prev => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        quotes: prev[fileName].quotes.map(q => 
          q.id === quoteId ? { ...q, form: { ...q.form, [key]: value } } : q
        )
      }
    }));
  };

  const confirmQuote = async (fileName, quoteId) => {
    const fileResult = results[fileName];
    const quote = fileResult?.quotes?.find(q => q.id === quoteId);
    if (!quote || quote.status !== 'extracted') return;
    
    setProcessing(prev => ({ ...prev, [quoteId]: true }));
    
    try {
      await axios.post(`${API}/devis/import-confirm`, quote.form, { headers: authHeaders });
      setResults(prev => ({
        ...prev,
        [fileName]: {
          ...prev[fileName],
          quotes: prev[fileName].quotes.map(q => 
            q.id === quoteId ? { ...q, status: 'success' } : q
          )
        }
      }));
      setSuccessCount(c => c + 1);
    } catch (err) {
      setResults(prev => ({
        ...prev,
        [fileName]: {
          ...prev[fileName],
          quotes: prev[fileName].quotes.map(q => 
            q.id === quoteId ? { ...q, status: 'error', error: err.response?.data?.detail || "Erreur" } : q
          )
        }
      }));
    } finally {
      setProcessing(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const confirmAll = async () => {
    for (const file of files) {
      const fileResult = results[file.name];
      if (!fileResult?.quotes) continue;
      
      for (const quote of fileResult.quotes) {
        if (quote.status === 'extracted' && quote.form?.vehicle_id) {
          await confirmQuote(file.name, quote.id);
        }
      }
    }
  };

  const reset = () => {
    setFiles([]);
    setResults({});
    setSuccessCount(0);
  };

  // Stats
  const pendingFiles = files.filter(f => !results[f.name]).length;
  const totalQuotes = Object.values(results).reduce((sum, r) => sum + (r.count || 0), 0);
  const extractedQuotes = Object.values(results).flatMap(r => r.quotes || []).filter(q => q.status === 'extracted').length;
  const successQuotes = Object.values(results).flatMap(r => r.quotes || []).filter(q => q.status === 'success').length;
  const errorQuotes = Object.values(results).flatMap(r => r.quotes || []).filter(q => q.status === 'error').length;
  const readyToConfirm = Object.values(results).flatMap(r => r.quotes || []).filter(q => 
    q.status === 'extracted' && q.form?.vehicle_id
  ).length;

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="import-title">Import de devis PDF</h1>
        <p className="text-gray-500 text-sm mt-1">
          Importez vos devis PDF. L'IA peut extraire <strong>plusieurs devis d'un même fichier</strong>.
        </p>
      </div>

      {/* Stats */}
      {files.length > 0 && (
        <div className="grid grid-cols-5 gap-3 mb-6">
          <div className="bg-white p-3 shadow-sm text-center">
            <p className="text-2xl font-bold text-gray-900">{files.length}</p>
            <p className="text-xs text-gray-500">Fichiers</p>
          </div>
          <div className="bg-blue-50 p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{totalQuotes}</p>
            <p className="text-xs text-gray-500">Devis détectés</p>
          </div>
          <div className="bg-amber-50 p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{extractedQuotes}</p>
            <p className="text-xs text-gray-500">À valider</p>
          </div>
          <div className="bg-green-50 p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{successQuotes}</p>
            <p className="text-xs text-gray-500">Créés</p>
          </div>
          <div className="bg-red-50 p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{errorQuotes}</p>
            <p className="text-xs text-gray-500">Erreurs</p>
          </div>
        </div>
      )}

      {/* Upload zone */}
      <div className="bg-white shadow-sm p-6 mb-6">
        <div 
          className="border-2 border-dashed rounded-lg p-8 text-center border-gray-200 hover:border-gray-300 transition-colors"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFilesChange(e.dataTransfer.files);
          }}
        >
          <Upload size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="font-semibold text-gray-700">Déposez vos fichiers PDF ici</p>
          <p className="text-sm text-gray-400 mb-3">Chaque PDF peut contenir plusieurs devis</p>
          <label className="inline-block px-5 py-2 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] cursor-pointer">
            Choisir des fichiers
            <input type="file" accept=".pdf" multiple onChange={e => handleFilesChange(e.target.files)} className="hidden" data-testid="file-input" />
          </label>
        </div>
      </div>

      {/* Action buttons */}
      {files.length > 0 && (
        <div className="flex gap-3 mb-6">
          <button onClick={analyzeAll} disabled={pendingFiles === 0 || Object.values(processing).some(p => p)}
            data-testid="analyze-all-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#D4A843] text-white text-sm font-semibold hover:bg-[#c49933] disabled:opacity-60">
            {Object.values(processing).some(p => p) ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Analyser tous ({pendingFiles} fichiers)
          </button>
          {readyToConfirm > 0 && (
            <button onClick={confirmAll} disabled={Object.values(processing).some(p => p)}
              data-testid="confirm-all-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60">
              <Save size={16} /> Créer tous les devis ({readyToConfirm})
            </button>
          )}
          <button onClick={reset} className="px-5 py-2.5 border border-gray-200 text-sm font-semibold hover:bg-gray-50">
            Recommencer
          </button>
        </div>
      )}

      {/* Files list */}
      {files.length > 0 && (
        <div className="space-y-4">
          {files.map(file => {
            const fileResult = results[file.name];
            const isProcessing = processing[file.name];
            const quotes = fileResult?.quotes || [];
            const isExpanded = fileResult?.expanded !== false;
            
            return (
              <div key={file.name} className="bg-white shadow-sm">
                {/* File header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className={
                      quotes.every(q => q.status === 'success') && quotes.length > 0 ? 'text-green-600' :
                      quotes.some(q => q.status === 'error') ? 'text-red-500' :
                      quotes.length > 0 ? 'text-amber-500' :
                      'text-gray-400'
                    } />
                    <div>
                      <p className="font-semibold text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-400">
                        {(file.size / 1024).toFixed(1)} Ko
                        {fileResult?.count > 0 && (
                          <span className="ml-2 text-amber-600 font-semibold">
                            → {fileResult.count} devis détecté{fileResult.count > 1 ? 's' : ''}
                          </span>
                        )}
                      </p>
                    </div>
                    {isProcessing && <Loader2 size={16} className="text-[#D4A843] animate-spin" />}
                  </div>
                  <div className="flex items-center gap-2">
                    {!fileResult && !isProcessing && (
                      <button onClick={() => analyzeFile(file)} data-testid={`analyze-${file.name}`}
                        className="px-3 py-1.5 text-xs font-semibold bg-[#D4A843] text-white hover:bg-[#c49933]">
                        Analyser
                      </button>
                    )}
                    {quotes.length > 0 && (
                      <button onClick={() => toggleExpanded(file.name)} 
                        className="p-1.5 text-gray-400 hover:text-gray-600">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                    <button onClick={() => removeFile(file.name)} title="Retirer"
                      className="p-1.5 text-gray-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Quotes list */}
                {isExpanded && quotes.length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {quotes.map((quote, idx) => (
                      <div key={quote.id} className={`p-4 ${quote.status === 'success' ? 'bg-green-50' : quote.status === 'error' ? 'bg-red-50' : 'bg-gray-50'}`}>
                        {/* Quote header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-500">Devis {idx + 1}</span>
                            {quote.status === 'success' && <CheckCircle size={14} className="text-green-600" />}
                            {quote.status === 'error' && <AlertCircle size={14} className="text-red-500" />}
                            {quote.data?.client_name && (
                              <span className="text-sm font-semibold text-gray-900">— {quote.data.client_name}</span>
                            )}
                          </div>
                          {quote.status === 'extracted' && (
                            <button onClick={() => confirmQuote(file.name, quote.id)} 
                              disabled={!quote.form?.vehicle_id || processing[quote.id]}
                              data-testid={`confirm-${quote.id}`}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
                              {processing[quote.id] ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                              Créer
                            </button>
                          )}
                        </div>

                        {/* Error message */}
                        {quote.status === 'error' && (
                          <p className="text-red-700 text-sm">{quote.error}</p>
                        )}

                        {/* Success message */}
                        {quote.status === 'success' && (
                          <p className="text-green-700 text-sm">✓ Devis créé avec succès</p>
                        )}

                        {/* Extracted data form */}
                        {quote.status === 'extracted' && (
                          <div className="grid grid-cols-4 gap-3 text-sm">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Véhicule *</label>
                              <select value={quote.form?.vehicle_id || ""} 
                                onChange={e => updateQuoteForm(file.name, quote.id, 'vehicle_id', e.target.value)}
                                className="w-full border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white">
                                <option value="">— Sélectionner —</option>
                                {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                              </select>
                              {quote.matchedVehicle && (
                                <p className="text-xs text-green-600 mt-0.5">✓ {quote.matchedVehicle.name}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Client *</label>
                              <input type="text" value={quote.form?.client_name || ""} 
                                onChange={e => updateQuoteForm(file.name, quote.id, 'client_name', e.target.value)}
                                className="w-full border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
                              <input type="email" value={quote.form?.client_email || ""} 
                                onChange={e => updateQuoteForm(file.name, quote.id, 'client_email', e.target.value)}
                                className="w-full border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Téléphone</label>
                              <input type="tel" value={quote.form?.client_telephone || ""} 
                                onChange={e => updateQuoteForm(file.name, quote.id, 'client_telephone', e.target.value)}
                                className="w-full border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Ville</label>
                              <input type="text" value={quote.form?.client_ville || ""} 
                                onChange={e => updateQuoteForm(file.name, quote.id, 'client_ville', e.target.value)}
                                className="w-full border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Date mariage</label>
                              <input type="date" value={quote.form?.date_mariage || ""} 
                                onChange={e => updateQuoteForm(file.name, quote.id, 'date_mariage', e.target.value)}
                                className="w-full border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Montant (€)</label>
                              <input type="number" value={quote.form?.montant || ""} 
                                onChange={e => updateQuoteForm(file.name, quote.id, 'montant', parseInt(e.target.value) || 0)}
                                className="w-full border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Statut</label>
                              <select value={quote.form?.statut || "réservé"} 
                                onChange={e => updateQuoteForm(file.name, quote.id, 'statut', e.target.value)}
                                className="w-full border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white">
                                <option value="réservé">Réservé</option>
                                <option value="devis">Devis</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Instructions */}
      {files.length === 0 && (
        <div className="bg-gray-50 p-6">
          <h3 className="font-semibold text-gray-800 mb-3">Comment ça marche ?</h3>
          <ol className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 bg-[#D4A843] text-white text-xs font-bold rounded-full flex items-center justify-center">1</span>
              <span>Sélectionnez vos fichiers PDF — <strong>plusieurs devis par fichier</strong> sont supportés</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 bg-[#D4A843] text-white text-xs font-bold rounded-full flex items-center justify-center">2</span>
              <span>L'IA analyse et détecte automatiquement tous les devis</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 bg-[#D4A843] text-white text-xs font-bold rounded-full flex items-center justify-center">3</span>
              <span>Vérifiez et corrigez les données si nécessaire</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 bg-[#D4A843] text-white text-xs font-bold rounded-full flex items-center justify-center">4</span>
              <span>Créez les devis un par un ou tous en même temps</span>
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}
